import type { NetworkError } from "@verbleif/shared/src/composables/Api/Error/NetworkError";
import type { ServerError } from "@verbleif/shared/src/composables/Api/Error/ServerError";

import type { AuthenticateOptions } from "@verbleif/shared/src/composables/Api/Plugin/Auth/AuthPlugin";

import { HttpStatusCode } from "@axios-extensions/plugins-api";
import { useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { api, installInterceptor } from "@verbleif/shared/src/composables/Api/api";
import { ClientError } from "@verbleif/shared/src/composables/Api/Error/ClientError";
import { AuthPath, createAuthenticatePlugin } from "@verbleif/shared/src/composables/Api/Plugin/Auth/AuthPlugin";
import { ACCESS_TOKEN_REVOKE_PATH } from "@verbleif/shared/src/composables/Api/Plugin/Auth/revokeAccessToken";
import { RefreshTokenResponseInterceptor } from "@verbleif/shared/src/composables/Api/Plugin/RefreshToken/RefreshTokenResponseInterceptor";
import { attemptDevSilentReauthorize } from "@verbleif/shared/src/composables/AuthStore/devSilentReauthorize";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useConfigStore } from "@verbleif/shared/src/composables/ConfigStore/useConfigStore";

import { onAuthenticateSuccess, storeTokens } from "@web/core/onAuthenticateSuccess";
import { openSessionExpiredDialog } from "@web/features/Auth/Accounts/useAccountDialogs";
import { useLogout } from "@web/features/Auth/Logout/useLogout";

const { authenticate } = createAuthenticatePlugin({
  axios: api,
  path: AuthPath.OAUTH_REFRESH_V2,
  storeTokens,
  onAuthenticateSuccess,
  onAuthenticateFailed,
});

// Refresh tokens rotate, so a second tab on the same account holds a stale token once the first
// tab refreshed. The account slot carries the rotated token; retrying from it once per failed
// refresh keeps that tab signed in instead of logging the whole account out. A successful
// refresh re-arms the retry, so long-lived tabs survive later rotations too; each retry needs a
// slot token that differs from the tab's own, so this cannot loop.
let retriedFromSlot = false;

function isInvalidGrant(e: unknown): boolean {
  return e instanceof ClientError && e.status === HttpStatusCode.BAD_REQUEST && e.response?.data?.error === "invalid_grant";
}

function rotatedSlotToken(): string | null {
  if (retriedFromSlot) {
    return null;
  }
  const slotToken = useAccountsStore().readRefreshToken();
  if (!slotToken || slotToken === useAuthStore().getRefreshToken.value) {
    return null;
  }
  return slotToken;
}

async function onAuthenticateFailed(
  e: ClientError | ServerError | NetworkError,
) {
  if (isInvalidGrant(e)) {
    // The authenticate plugin still holds its in-flight promise here; `refreshToken()` performs
    // the slot retry once this failure has settled.
    if (rotatedSlotToken()) {
      console.log("[Refresh] Invalid grant — account slot holds a rotated token, retrying");
      return;
    }

    if (attemptDevSilentReauthorize()) {
      console.log("[Refresh] Invalid grant — attempting silent re-authorize");
      return;
    }

    // The slot stays listed as expired so the account menu offers to sign in again.
    useAccountsStore().markExpired();
    if (openSessionExpiredDialog()) {
      console.log("[Refresh] Invalid grant, other accounts available, asking what to do");
      return;
    }

    console.log("[Refresh] Invalid grant, redirecting to login");
    const { logout } = useLogout();
    return logout({ logoutAuthServer: false });
  }
}

export async function refreshToken(options: AuthenticateOptions = {}) {
  console.log("Refreshing token");
  const authStore = useAuthStore();
  const { config } = useConfigStore();

  if (!authStore.getRefreshToken.value) {
    console.error("No refresh token set.");
    throw new Error("No refresh token set.");
  }
  if (!config.value) {
    console.error("Config not set");
    throw new Error("Config not set");
  }
  try {
    await authenticate({
      grant_type: "refresh_token",
      refresh_token: authStore.getRefreshToken.value,
      client_id: `${config.value.loginClientId}`, // Replace with your client ID.
      redirect_uri: `${config.value.loginRedirect}/oauth/v2/callback`, // Replace with your callback URL.
    }, options);
    retriedFromSlot = false;
  } catch (e) {
    const slotToken = isInvalidGrant(e) ? rotatedSlotToken() : null;
    if (!slotToken) {
      throw e;
    }
    retriedFromSlot = true;
    authStore.setRefreshToken(slotToken, true);
    return refreshToken(options);
  }
}

const refreshTokenInterceptorPlugin = RefreshTokenResponseInterceptor(
  api,
  // A request that got a 401 only needs the new tokens to retry. Waiting for the whole session load would hang
  // when that load joins this same request or gets a 401 of its own: it would wait on the refresh it is part of.
  () => refreshToken({ waitFor: "tokens" }),
  // A 401 on the revoke call means the session is already gone; refreshing would mint a fresh
  // token mid-logout (or fail and start a second logout). The guest report page (a guest link) stays off the
  // refresh path through `skipTokenRefresh` on each of its calls (`guestLinkRequestConfig`).
  ["/oauth/v2/token", ACCESS_TOKEN_REVOKE_PATH],
);
installInterceptor(refreshTokenInterceptorPlugin);
